import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Pencil, UserRound, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CustomerSearchResult } from "@tilt/pos-sdk";

import { getPosClient } from "../../lib/api";
import { isLikelyEmail } from "../../lib/validate";
import { Field, TextArea, TextInput } from "../ui/Field";
import type { CustomerLookupValue } from "./customer-lookup-helpers";

/**
 * Phone-first customer lookup.
 *
 * UX flow the user asked for:
 *   1. Cashier types the phone number first (the most-recallable
 *      identifier for a regular).
 *   2. As they type, debounced search hits ``pos_customer_tbl`` over
 *      the active scope.
 *   3. Single match → auto-attach + collapse to a green "attached"
 *      badge with name + phone + email; the rest of the form
 *      pre-fills.
 *   4. Multiple matches → small picker list ("Pick the right one").
 *      Click selects + auto-attaches.
 *   5. Zero matches once digits ≥ 7 → "New customer" banner. The
 *      cashier types name + email manually.
 *
 * After attachment the cashier can still hit the X to detach (typo
 * fix). Detaching re-opens the phone field for editing.
 *
 * Value shape + helpers live in customer-lookup-helpers.ts.
 */

export interface CustomerLookupProps {
  readonly value: CustomerLookupValue;
  readonly onChange: (next: CustomerLookupValue) => void;
  readonly disabled?: boolean;
}

const SEARCH_DEBOUNCE_MS = 250;
const MIN_PHONE_DIGITS_FOR_SEARCH = 4;
// Auto-attach kicks in only at this digit count or higher AND only
// when the typed digits actually equal the candidate's normalized
// digits. Below this we keep the candidate visible in the picker
// but never collapse it into the form - typing 6 digits of a
// 10-digit phone shouldn't jump the cashier mid-stream.
const MIN_PHONE_DIGITS_FOR_AUTO_ATTACH = 10;

/** Decide whether the typed-digit string is a "full" match against a
 *  candidate phone - i.e. the cashier finished typing the number, not
 *  still mid-stream. The backend search is substring/trigram so a 6-
 *  digit typed value can return a single match, but auto-attaching on
 *  that pulls focus away from the phone field before the cashier
 *  meant to commit. We accept three forms of "full":
 *
 *    1. typed digits === candidate's normalized digits (exact)
 *    2. typed length >= 10 AND candidate ends with typed digits
 *       (US/CA without country code: typed "5555550100", candidate
 *       "+15555550100")
 *    3. typed length >= 10 AND typed ends with candidate digits
 *       (cashier added country code, candidate stored without it)
 *
 *  Anything shorter - or a substring elsewhere in the candidate -
 *  keeps the suggestion in the picker so the cashier can confirm
 *  with a tap or keep typing past it. */
function isFullPhoneMatch(candidatePhone: string | null, typedDigits: string): boolean {
  if (!candidatePhone) return false;
  if (typedDigits.length < MIN_PHONE_DIGITS_FOR_AUTO_ATTACH) return false;
  const candidateDigits = candidatePhone.replace(/\D/g, "");
  if (candidateDigits.length === 0) return false;
  if (candidateDigits === typedDigits) return true;
  if (candidateDigits.endsWith(typedDigits)) return true;
  if (typedDigits.endsWith(candidateDigits)) return true;
  return false;
}

export function CustomerLookup({ value, onChange, disabled }: CustomerLookupProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Customer the cashier just manually detached. The auto-attach
  // effect would otherwise immediately re-attach them on the next
  // render - phone is still populated, the cached query result still
  // shows the same single match, so without this gate clicking the
  // X looks like nothing happens (the green pill flickers off and
  // back on within one render).
  //
  // Guard stores the DIGIT string of the phone at detach time, not the
  // full formatted string. This prevents the gate from clearing the
  // moment a formatting character is deleted: as long as the typed
  // digits are a prefix of the stored digits OR the stored digits are
  // a prefix of the typed digits (country code added), the guard stays
  // active. Only a genuinely different number clears it.
  const [detachedId, setDetachedId] = useState<string | null>(null);
  const [detachedAtDigits, setDetachedAtDigits] = useState<string>("");

  // Track the name/email stored in the customer record at the time of
  // attach so we can detect cashier edits and offer a "Save" button.
  const originalRef = useRef<{ name: string; email: string } | null>(null);

  // When the component mounts with a customer already attached (e.g. wizard
  // draft restore), initialise the snapshot so the save banner can detect
  // edits from the restored values.
  useEffect(() => {
    if (value.selectedCustomerId !== null && originalRef.current === null) {
      originalRef.current = { name: value.name, email: value.email };
    }
  }, [value.selectedCustomerId, value.name, value.email]);

  // Debounced phone for the search query - prevents a request per
  // keystroke. Only digits count toward the threshold so "(415)"
  // doesn't fire before the cashier has typed enough.
  const [debouncedPhone, setDebouncedPhone] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedPhone(value.phone.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [value.phone]);

  // Clear the just-detached gate when the cashier types a number whose
  // digits can no longer plausibly match the detached customer's phone.
  // "Could match" means the typed digits are a leading prefix of the
  // original OR the original is a prefix of the typed string (country
  // code added). Deleting a formatting character (space, dash) keeps
  // the gate because the digit string stays unchanged.
  useEffect(() => {
    if (detachedId == null) return;
    const currentDigits = value.phone.replace(/\D/g, "");
    if (
      !detachedAtDigits.startsWith(currentDigits) &&
      !currentDigits.startsWith(detachedAtDigits)
    ) {
      setDetachedId(null);
      setDetachedAtDigits("");
    }
  }, [value.phone, detachedAtDigits, detachedId]);

  const phoneDigits = debouncedPhone.replace(/\D/g, "");
  const searchEnabled =
    !disabled && !value.selectedCustomerId && phoneDigits.length >= MIN_PHONE_DIGITS_FOR_SEARCH;

  const query = useQuery({
    queryKey: ["pos", "customers", "phone", debouncedPhone],
    queryFn: () => getPosClient().searchCustomers(debouncedPhone, 8),
    enabled: searchEnabled,
    staleTime: 30_000,
  });

  // Memoise so the auto-attach effect's dependency array stays
  // referentially stable across renders - react-query returns the
  // same array reference between fetches but the optional-chain
  // produces a fresh ``[]`` when undefined, which would re-trigger
  // the effect every render and refire the attach dispatch.
  //
  // Filter out junk customer rows whose phone column is too short to
  // be a real phone (e.g. ``"5416"`` from inventory imports that
  // accidentally wrote stock numbers into ``customer_phone``).
  // The cashier should never see those as lookup hits - they're
  // unreachable for SMS / pay-by-text and re-attaching one
  // poisons the order's customer record. Keep rows that have a
  // recognisable name OR email even when the phone is short, so
  // the cashier can still find a regular by typing their first
  // name. (2026-05-02 dogfood: junk "5416" customer surfaced as
  // a top match next to a real "Ram" record.)
  const matches: readonly CustomerSearchResult[] = useMemo(() => {
    const raw = query.data?.customers ?? [];
    return raw.filter((c) => {
      const phoneDigitsLen = (c.phone ?? "").replace(/\D/g, "").length;
      const hasUsablePhone = phoneDigitsLen >= MIN_PHONE_DIGITS_FOR_AUTO_ATTACH;
      const hasName = !!(c.first_name?.trim() || c.last_name?.trim());
      const hasEmail = !!c.email?.trim();
      return hasUsablePhone || hasName || hasEmail;
    });
  }, [query.data]);

  // Auto-attach on a true full-phone match - backend search is
  // substring/trigram, so a partial like "541602" can return a
  // single match such as "+15555416025"; if we attached on that
  // the cashier would auto-jump to amount mid-typing (regression
  // reported 2026-04-29).
  //
  // ``isFullPhoneMatch`` accepts three forms (see its JSDoc):
  // exact equality, suffix match (cashier omitted country code),
  // or candidate-as-suffix (cashier added country code). Anything
  // shorter than 10 digits OR a substring elsewhere in the
  // candidate keeps the result in the picker instead.
  //
  // Skip auto-attach when:
  //   (a) someone is already attached
  //   (b) a search is still in flight (avoid flicker)
  //   (c) the single match is the customer the cashier *just*
  //       manually detached - re-attaching one render after the X
  //       click would make the button look broken. The picker
  //       handles this case below by surfacing the same single
  //       row as a clickable card so the cashier can choose to
  //       re-attach.
  //   (d) the typed digits don't fully match the candidate (the
  //       cashier is still mid-typing - ``showPicker`` surfaces the
  //       suggestion as a clickable card so they can confirm
  //       explicitly without losing the find-as-you-type cue)
  useEffect(() => {
    if (value.selectedCustomerId) return;
    if (!searchEnabled || query.isFetching) return;
    if (matches.length !== 1) return;
    const c = matches[0];
    if (!c) return;
    if (detachedId === c.id) return;
    if (!isFullPhoneMatch(c.phone, phoneDigits)) return;
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || value.name;
    const email = c.email ?? value.email;
    originalRef.current = { name, email };
    onChange({
      selectedCustomerId: c.id,
      name,
      phone: c.phone ?? value.phone,
      email,
      description: value.description,
    });
  }, [matches, query.isFetching, searchEnabled, value, onChange, detachedId, phoneDigits]);

  const attach = (c: CustomerSearchResult) => {
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || value.name;
    const email = c.email ?? value.email;
    originalRef.current = { name, email };
    onChange({
      selectedCustomerId: c.id,
      name,
      phone: c.phone ?? value.phone,
      email,
      description: value.description,
    });
  };

  const detach = () => {
    if (value.selectedCustomerId) {
      setDetachedId(value.selectedCustomerId);
      setDetachedAtDigits(value.phone.replace(/\D/g, ""));
    }
    originalRef.current = null;
    onChange({ ...value, selectedCustomerId: null });
  };

  const setPhone = (next: string) => onChange({ ...value, phone: next, selectedCustomerId: null });
  // Name and email edits intentionally keep selectedCustomerId so the
  // order still links to the existing customer record. The cashier can
  // save the correction back to the customer record via the "Save"
  // button that appears when values differ from the original.
  const setName = (next: string) => onChange({ ...value, name: next });
  const setEmail = (next: string) => onChange({ ...value, email: next });
  const setDescription = (next: string) => onChange({ ...value, description: next });

  const emailInvalid = value.email.trim().length > 0 && !isLikelyEmail(value.email);

  const updateMutation = useMutation({
    mutationFn: (body: { name?: string; email?: string }) =>
      getPosClient().updateCustomer(value.selectedCustomerId!, body),
    onSuccess: () => {
      if (originalRef.current) {
        originalRef.current = { name: value.name, email: value.email };
      }
    },
  });

  const nameChanged =
    !!value.selectedCustomerId &&
    originalRef.current !== null &&
    value.name !== originalRef.current.name;
  const emailChanged =
    !!value.selectedCustomerId &&
    originalRef.current !== null &&
    value.email !== originalRef.current.email;
  const showSaveCustomer = (nameChanged || emailChanged) && !disabled;

  // Render branching states based on (attached?, searching?, results).
  // After phone digits cross the threshold we're always in one of:
  //   * attached - green pill, hide the rest of the form behind a
  //     read-only summary
  //   * searching - phone field stays, small "looking up…" pill
  //   * multi - phone field + a picker list
  //   * none - phone field + "new customer" banner; surface name +
  //     email + description fields below
  // Surface the picker any time the auto-attach effect chose NOT to
  // collapse a result into the form. We compute the same gates the
  // effect uses and show the picker for any non-empty result that
  // *won't* auto-attach. Three concrete cases:
  //   * matches.length > 1 - multi-match, cashier picks one
  //   * matches.length === 1 BUT typed digits aren't the full match
  //     - cashier is still mid-typing; suggestion stays visible
  //   * matches.length === 1, typed IS the full match, but the
  //     candidate is the customer the cashier *just* detached -
  //     auto-attach skips to keep the X button feeling responsive,
  //     and without showing the picker the cashier would be left
  //     staring at an empty surface (Copilot review PR #65). The
  //     picker re-renders the row as clickable so they can
  //     deliberately re-attach if they meant to.
  const onlyMatch = matches.length === 1 ? matches[0] : null;
  const onlyMatchIsFull = onlyMatch != null && isFullPhoneMatch(onlyMatch.phone, phoneDigits);
  const willAutoAttach = onlyMatch != null && onlyMatchIsFull && detachedId !== onlyMatch.id;
  const showPicker =
    !value.selectedCustomerId &&
    searchEnabled &&
    !query.isFetching &&
    matches.length > 0 &&
    !willAutoAttach;
  const showSearching = !value.selectedCustomerId && searchEnabled && query.isFetching;
  const showNoMatch =
    !value.selectedCustomerId &&
    searchEnabled &&
    !query.isFetching &&
    matches.length === 0 &&
    phoneDigits.length >= 7;
  const showRestOfForm =
    value.selectedCustomerId !== null ||
    !searchEnabled ||
    showNoMatch ||
    phoneDigits.length < MIN_PHONE_DIGITS_FOR_SEARCH;

  return (
    <div className="space-y-3" ref={containerRef}>
      {value.selectedCustomerId ? (
        <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-700" />
            <div>
              <div className="font-medium text-emerald-900">
                {value.name || t("sale.customerUnnamed")}
              </div>
              <div className="text-xs text-emerald-800">
                {[value.phone, value.email].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={detach}
            className="rounded p-1 text-emerald-800 hover:bg-emerald-100"
            aria-label={t("sale.detachCustomer")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <Field
          label={t("sale.customerPhone")}
          hint={t("sale.customerPhoneHint")}
          // Inline validation: empty is fine (the phone field itself
          // is optional on cash orders), but anything 1-9 digits is
          // bogus - surface the error before submit so the cashier
          // doesn't get a generic 400 from the order create. The
          // backend (orders.py:_find_or_create_customer) is the
          // safety net that rejects with the same threshold.
          error={
            phoneDigits.length > 0 && phoneDigits.length < 10
              ? t("sale.customerPhoneTooShort")
              : null
          }
        >
          <TextInput
            type="tel"
            autoComplete="tel"
            value={value.phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={64}
            disabled={disabled}
            placeholder={t("sale.customerPhonePlaceholder")}
            invalid={phoneDigits.length > 0 && phoneDigits.length < 10}
            autoFocus
          />
        </Field>
      )}

      {showSearching && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 rounded-md border border-line bg-surface-muted px-3 py-2 text-xs text-foreground"
        >
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-line-strong border-t-slate-700" />
          {t("sale.customerLookupSearching")}
        </div>
      )}

      {showPicker && (
        <div>
          {/* Count-agnostic header - picker now shows for both single
              partial-match (cashier still typing) and multi-match,
              so a "Multiple matches" label would mis-read at count=1. */}
          <p className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            {t("sale.customerLookupMatchesHeader", { count: matches.length })}
          </p>
          <ul className="divide-y divide-line/60 overflow-hidden rounded-xl bg-surface shadow-elev-1">
            {matches.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => attach(c)}
                  className="flex min-h-tap w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                >
                  <UserRound className="h-4 w-4 flex-shrink-0 text-faint" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-strong">
                      {[c.first_name, c.last_name].filter(Boolean).join(" ") ||
                        c.email ||
                        c.phone ||
                        t("sale.customerUnnamed")}
                    </div>
                    <div className="truncate font-mono text-xs text-muted">
                      {[c.phone, c.email].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showNoMatch && (
        <div className="rounded-md border border-line bg-surface-muted px-3 py-2 text-xs text-foreground">
          <span className="font-medium">{t("sale.customerLookupNoMatchTitle")}</span>{" "}
          {t("sale.customerLookupNoMatchBody")}
        </div>
      )}

      {showRestOfForm && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t("sale.customerName")} className="sm:col-span-1">
            <TextInput
              type="text"
              autoComplete="name"
              value={value.name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              disabled={disabled}
              placeholder={t("sale.customerNamePlaceholder")}
            />
          </Field>
          <Field
            label={t("sale.customerEmail")}
            error={emailInvalid ? t("sale.customerEmailInvalid") : null}
            className="sm:col-span-1"
          >
            <TextInput
              type="email"
              autoComplete="email"
              value={value.email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={320}
              disabled={disabled}
              invalid={emailInvalid}
              placeholder={t("sale.customerEmailPlaceholder")}
            />
          </Field>
        </div>
      )}

      {(showSaveCustomer || updateMutation.isError) && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
            <span className="text-amber-800">{t("sale.customerDetailsChanged")}</span>
            <button
              type="button"
              onClick={() =>
                updateMutation.mutate({
                  ...(nameChanged ? { name: value.name.trim() } : {}),
                  ...(emailChanged ? { email: value.email.trim() } : {}),
                })
              }
              disabled={updateMutation.isPending || emailInvalid}
              className="ml-3 flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
            >
              <Pencil className="h-3 w-3" />
              {updateMutation.isPending ? t("sale.customerSaving") : t("sale.customerSaveChanges")}
            </button>
          </div>
          {updateMutation.isError && (
            <p className="text-xs text-red-600">{t("sale.customerSaveFailed")}</p>
          )}
        </div>
      )}

      {/* Description always visible - cashier should be able to type
          "what is the customer paying for" while a phone-number search
          is still in flight (so they can populate description + amount
          in parallel without waiting on the lookup). Doesn't depend on
          attached / searching / multi-match state.

          Multiline-capable textarea (rows=1 collapsed shape so it
          reads like the inputs above) so an Inventory deep-link's
          ``initialDescription`` (Year/Make/Model + Color line + VIN
          + Engine + Miles, joined with ``\n``) renders across as
          many rows as it needs instead of being clipped to one
          line. Auto-grows on typing too. */}
      <Field label={t("sale.description")}>
        <TextArea
          value={value.description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={disabled}
          placeholder={t("sale.descriptionPlaceholder")}
        />
      </Field>
    </div>
  );
}
